import dayjs from 'dayjs'
import {
  findRefundableStripePayments,
  findRefundableSwishPayments,
  findRefundableVippsPayments,
  shouldIncludeStoredAdministrationFee
} from './PaymentHelper'
import {
  Activity,
  Client,
  Enrollment,
  EnrollmentPublic,
  EnrollmentStatus,
  EnrollmentWithPayment,
  PayFor,
  Payment,
  VatData
} from '../types/GrooverTypes'
import { debugLog } from '../utils/debug'

export const obfuscateEmail = (email: string) => {
  const [user, domain] = email.split('@')
  return `${user.slice(0, 3)}...@${domain}`
}

export const obfuscateName = (name: string) => {
  const firstThreeCharsOfFirstName = name.slice(0, 3)
  const firstThreeCharsOfLastName = name.split(' ')[1]?.slice(0, 3) ?? ''

  if (!firstThreeCharsOfLastName) return `${firstThreeCharsOfFirstName}...`

  return `${firstThreeCharsOfFirstName}... ${firstThreeCharsOfLastName}...`
}

export const obfuscatePhoneNumber = (phoneNumber: string) => {
  const lastThreeDigits = phoneNumber.slice(-3)

  return `*****${lastThreeDigits}`
}

export const obfuscateSSN = (ssn: string) => `${ssn.slice(0, 2)}***`

/**
 * When multiple unfinished enrollments match, pick one to offer for resume.
 * V4 couples share enrollmentGroupId (one logical registration); other duplicates are separate groups.
 * Prefer the most recently created group; within a couple group prefer the primary phone's doc.
 */
export const pickPreferredUnfinishedEnrollment = (
  matches: Enrollment[],
  mainUserId?: string
): Enrollment | undefined => {
  if (matches.length === 0) return undefined
  if (matches.length === 1) return matches[0]

  const groupKey = (enrollment: Enrollment) => enrollment.enrollmentGroupId ?? enrollment.id
  const groups = new Map<string, Enrollment[]>()

  for (const enrollment of matches) {
    const key = groupKey(enrollment)
    const group = groups.get(key) ?? []
    group.push(enrollment)
    groups.set(key, group)
  }

  const pickFromGroup = (group: Enrollment[]): Enrollment => {
    if (group.length === 1) return group[0]
    if (mainUserId) {
      const primaryDoc = group.find((enrollment) => enrollment.mainUser?.id === mainUserId)
      if (primaryDoc) return primaryDoc
    }
    return group.sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())[0]
  }

  const representatives = [...groups.values()].map(pickFromGroup)
  representatives.sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
  return representatives[0]
}

export const obfuscateEnrollment = (enrollment: Enrollment) => {
  const obfuscatedMainUser = {
    ...enrollment.mainUser,
    name: obfuscateName(enrollment.mainUser.name),
    email: obfuscateEmail(enrollment.mainUser.email),
    phone: obfuscatePhoneNumber(enrollment.mainUser.phone),
    nonObfuscatedName: enrollment.mainUser.name
  }
  if (enrollment.mainUser.ssn) obfuscatedMainUser.ssn = obfuscateSSN(enrollment.mainUser.ssn)

  const obfuscatedParnerUser = enrollment.partnerUser
    ? {
        ...enrollment.partnerUser,
        name: obfuscateName(enrollment.partnerUser.name),
        email: obfuscateEmail(enrollment.partnerUser.email),
        phone: obfuscatePhoneNumber(enrollment.partnerUser.phone),
        nonObfuscatedName: enrollment.partnerUser.name
      }
    : undefined
  if (obfuscatedParnerUser && enrollment.partnerUser?.ssn)
    obfuscatedParnerUser.ssn = obfuscateSSN(enrollment.partnerUser.ssn)

  return {
    ...enrollment,
    mainUser: obfuscatedMainUser,
    partnerUser: obfuscatedParnerUser
  }
}

/**
 * What the per-user payment maths below actually touches. Narrower than `Enrollment` on purpose:
 * these are called with projected enrollments (see `REMINDER_ENROLLMENT_FIELDS` in
 * RegistrationDBHandler), which omit the bulky fields. A full `Enrollment` still satisfies it,
 * so every existing caller is unaffected.
 */
export type EnrollmentPaymentsView = Pick<Enrollment, 'mainUser' | 'partnerUser' | 'payments'>

/** `EnrollmentPaymentsView` plus what pricing/confirmation checks need. */
export type EnrollmentPaymentStatusView = EnrollmentPaymentsView & Pick<Enrollment, 'priceDetails' | 'statuses'>

export const getUserPayments = (enrollment: EnrollmentPaymentsView, payFor: PayFor) => {
  const userPayments = enrollment.payments
    .filter((p) => p.type == 'pay') // only actuall payments
    .reduce(
      (arr, payment) => {
        let paid = 0
        // Handle backwards compatibility - treat payments with no userId
        // or userId that doesn't match main/partner as payment for main user
        const isMainUserPayment = payment.userId === enrollment.mainUser.id
        const isPartnerUserPayment = payment.userId === enrollment.partnerUser?.id
        const isSameUserIdScenario = enrollment.mainUser.id === enrollment.partnerUser?.id && isMainUserPayment
        const isUnassignedPayment = !payment.userId || (!isMainUserPayment && !isPartnerUserPayment)

        if (payFor === PayFor.Both) {
          paid = payment.totalAmount
          arr.push({ payment, paid })
        } else if (payFor === PayFor.Main && (isMainUserPayment || isUnassignedPayment)) {
          // treat mainUser OR unassigned payments as mainUser payments
          paid = payment.totalAmount
          arr.push({ payment, paid })
        } else if (payFor === PayFor.Partner && (isPartnerUserPayment || isSameUserIdScenario)) {
          // If it's a same userId scenario, handle payments correctly for Partner context
          paid = payment.totalAmount
          arr.push({ payment, paid })
        }

        return arr
      },
      [] as Array<{ payment: Payment; paid: number }>
    )

  return userPayments
}

export const getUserRefundedAmount = (enrollment: EnrollmentPaymentsView, payFor: PayFor): number => {
  return (
    enrollment.payments
      .filter((p) => p.type === 'refund')
      .reduce((sum, payment) => {
        // Handle backwards compatibility - treat refunds with no userId
        // or userId that doesn't match main/partner as refund for main user
        const isMainUserRefund = payment.userId === enrollment.mainUser.id
        const isPartnerUserRefund = payment.userId === enrollment.partnerUser?.id
        const isSameUserIdScenario = enrollment.mainUser.id === enrollment.partnerUser?.id && isMainUserRefund
        const isUnassignedRefund = !payment.userId || (!isMainUserRefund && !isPartnerUserRefund)

        if (payFor === PayFor.Both) {
          return sum + payment.totalAmount
        }

        if (payFor === PayFor.Main && (isMainUserRefund || isUnassignedRefund)) {
          return sum + payment.totalAmount
        }

        if (payFor === PayFor.Partner && (isPartnerUserRefund || isSameUserIdScenario)) {
          return sum + payment.totalAmount
        }

        return sum
      }, 0) || 0
  )
}

export const getUserNetPaidAmount = (enrollment: EnrollmentPaymentsView, payFor: PayFor): number => {
  const userPayments = getUserPayments(enrollment, payFor)
  const totalPaid = userPayments.reduce((sum, payEntry) => sum + payEntry.paid, 0)
  const refunded = Math.abs(getUserRefundedAmount(enrollment, payFor))
  return totalPaid - refunded
}

export const getUserRefundableAmount = (enrollment: Enrollment, payFor: PayFor): number => {
  return Math.max(0, getUserNetPaidAmount(enrollment, payFor))
}

export type RefundMethodKind = 'manual' | 'swish' | 'vipps' | 'stripe'

/**
 * Every refund method that can actually be completed for this enrollment, used as the single
 * source of truth for which refund options the admin UI offers.
 *
 * Provider methods are additive — a Swish payment and a Stripe payment are separate pools of
 * money, so if both are still refundable both are offered.
 *
 * `'manual'` is only ever returned ALONE, and only when no provider method applies. That is not a
 * UI preference: `buildManualRefundsForEnrollment`
 * (admin-web/src/features/featureEnrollments/utils/manualPayment.ts:128) hard-blocks manual
 * refunds with `error: 'Provider refund required'` whenever `hasProviderRefundablePayments` is
 * true. Offering manual next to a provider option would therefore produce a UI choice that always
 * fails on submit.
 *
 * Lives here rather than in PaymentHelper because it needs `getUserRefundableAmount` (this file)
 * and EnrollmentHelper already imports PaymentHelper — putting it in PaymentHelper would close an
 * import cycle between two modules that export `const` arrow functions (TDZ-fragile at
 * module-eval time).
 */
export const getApplicableRefundMethods = (enrollment: Enrollment): RefundMethodKind[] => {
  const providers: RefundMethodKind[] = []
  if (findRefundableSwishPayments(enrollment).length > 0) providers.push('swish')
  if (findRefundableStripePayments(enrollment).length > 0) providers.push('stripe')
  if (findRefundableVippsPayments(enrollment).length > 0) providers.push('vipps')

  if (providers.length > 0) return providers

  return getUserRefundableAmount(enrollment, PayFor.Both) > 0 ? ['manual'] : []
}

/**
 * True when the enrollment had a real payment and refunds have since brought the net paid
 * amount (across both users) to zero or below. Never true for free/never-paid enrollments.
 */
export const isEnrollmentFullyRefunded = (enrollment: Enrollment): boolean => {
  const totalPaid = getUserPayments(enrollment, PayFor.Both).reduce((sum, entry) => sum + entry.paid, 0)
  if (totalPaid <= 0) return false
  return getUserNetPaidAmount(enrollment, PayFor.Both) <= 0
}

export const getToBePaidData = (
  enrollment: EnrollmentWithPayment,
  includeAdminFee: boolean = true
): Record<PayFor, number> => {
  let toBePaidData = [PayFor.Both, PayFor.Main, PayFor.Partner].reduce(
    (acc, payFor) => {
      acc[payFor] = enrollment ? getTotalUnpaidPaymentsAmount(enrollment, payFor, includeAdminFee) : 0

      return acc
    },
    {} as Record<PayFor, number>
  )
  return toBePaidData
}

/**
 * Helper to check if an activity is confirmed based on enrollment.statuses
 * Returns true if:
 * - No statuses array exists (legacy enrollments - all activities are assumed confirmed)
 * - Activity status is 'confirmed'
 * Returns false if activity status is 'pending' or any other non-confirmed status
 */
export const isActivityConfirmed = (enrollment: Pick<Enrollment, 'statuses'>, activityId: string): boolean => {
  // If no statuses array, assume legacy enrollment where all activities are confirmed
  if (!enrollment.statuses || enrollment.statuses.length === 0) {
    return true
  }

  const activityStatus = enrollment.statuses.find((s) => s.activityId === activityId)

  // If activity has no status entry, default to confirmed (legacy behavior)
  if (!activityStatus) {
    return true
  }

  // Only confirmed activities should be included in payment calculations
  return activityStatus.status === EnrollmentStatus.Confirmed
}

/**
 * Subevents this enrollment is confirmed for.
 * Confirmation status lives on the enrollment (per activityId), not per user.
 */
export const getConfirmedSubeventsForEnrollment = (
  enrollment: Pick<Enrollment, 'activityIds' | 'statuses'>,
  subevents: Activity[]
): Activity[] =>
  subevents.filter(
    (subevent) => (enrollment.activityIds ?? []).includes(subevent.id) && isActivityConfirmed(enrollment, subevent.id)
  )

/** Map legacy Refunded → CancelledWithRefund for display, exclusion, aggregation, and filters. */
export const normalizeEnrollmentStatus = (status: EnrollmentStatus): EnrollmentStatus =>
  status === EnrollmentStatus.Refunded ? EnrollmentStatus.CancelledWithRefund : status

/** Statuses that must not contribute to value/due / registration graph calculations. */
export const EXCLUDED_FROM_VALUE_ENROLLMENT_STATUSES: EnrollmentStatus[] = [
  EnrollmentStatus.Deleted,
  EnrollmentStatus.Cancelled,
  EnrollmentStatus.Refunded, // legacy
  EnrollmentStatus.ClassCancelled,
  EnrollmentStatus.CancelledWithRefund,
  EnrollmentStatus.CancelledWithCredit
]

export const isEnrollmentStatusExcludedFromValue = (status: EnrollmentStatus): boolean =>
  EXCLUDED_FROM_VALUE_ENROLLMENT_STATUSES.includes(status)

/** Person-cancel, class-cancel, refund, and credit terminals (not Deleted). */
export const isEnrollmentStatusCancelled = (status: EnrollmentStatus): boolean => {
  const normalized = normalizeEnrollmentStatus(status)
  return isEnrollmentStatusExcludedFromValue(normalized) && normalized !== EnrollmentStatus.Deleted
}

/**
 * True when the enrollment, or every activity on it, is a cancelled terminal.
 *
 * IMPORTANT: when `statuses` is non-empty it is authoritative — a stale top-level `status` must not
 * override live per-activity entries. A partial `statuses` array (e.g. only the one activity an admin
 * just cancelled) does NOT make the whole enrollment cancelled: the entries must cover every activity
 * on the enrollment AND all be cancelled. If the activity list is unknown we fall back to
 * "every entry cancelled ⇒ cancelled".
 */
export const isEnrollmentCancelled = (
  enrollment: Pick<Enrollment, 'status' | 'statuses'> & Partial<Pick<Enrollment, 'activityIds' | 'activities'>>
): boolean => {
  const statuses = enrollment.statuses || []
  if (statuses.length === 0) {
    return !!enrollment.status && isEnrollmentStatusCancelled(enrollment.status)
  }
  if (!statuses.every((s) => isEnrollmentStatusCancelled(s.status))) return false

  const activityIds = enrollment.activityIds?.length
    ? enrollment.activityIds
    : (enrollment.activities || []).map((activity) => activity.id)
  // Unknown activity list: keep the legacy "every entry cancelled ⇒ cancelled" behaviour.
  if (activityIds.length === 0) return true

  const coveredActivityIds = new Set(statuses.map((s) => s.activityId))
  return activityIds.every((id) => coveredActivityIds.has(id))
}

/**
 * Helper to check if an activity is excluded from value/due calculations based on enrollment.statuses.
 *
 * IMPORTANT: For UI/report calculations we only exclude an activity when there is an explicit status
 * entry for it (statuses-only rule). If no entry exists, it is NOT excluded.
 */
export const isActivityExcludedFromValue = (enrollment: Enrollment, activityId: string): boolean => {
  if (!enrollment.statuses || enrollment.statuses.length === 0) {
    return false
  }
  const activityStatus = enrollment.statuses.find((s) => s.activityId === activityId)
  if (!activityStatus) return false
  return isEnrollmentStatusExcludedFromValue(activityStatus.status)
}

/**
 * Helper to check whether the enrollment has at least one non-excluded activity with a
 * positive finalPrice that is still awaiting approval (i.e. not yet confirmed).
 *
 * Used by the registration Step 4 UI: when this is true AND the confirmed balance is 0,
 * the user has nothing to pay yet — show the "awaiting approval" block. When this is true
 * AND the confirmed balance is > 0 (partly-approved), the user CAN pay for the approved
 * portion immediately and should see the payment UI plus a pending-activities banner.
 */
export const hasPendingPricedActivities = (enrollment: Enrollment): boolean => {
  if (!enrollment?.priceDetails) return false
  return enrollment.priceDetails.some(
    (pd) =>
      pd.finalPrice > 0 &&
      !isActivityExcludedFromValue(enrollment, pd.activityId) &&
      !isActivityConfirmed(enrollment, pd.activityId)
  )
}

/**
 * Helper to check if an activity is deleted based on enrollment.statuses.
 *
 * IMPORTANT: For UI/report calculations we only treat an activity as deleted when there is an
 * explicit status entry for it (statuses-only rule). If no entry exists, it is NOT deleted.
 */
export const isActivityDeleted = (enrollment: Enrollment, activityId: string): boolean => {
  if (!enrollment.statuses || enrollment.statuses.length === 0) {
    return false
  }
  const activityStatus = enrollment.statuses.find((s) => s.activityId === activityId)
  if (!activityStatus) return false
  return activityStatus.status === EnrollmentStatus.Deleted
}

export const getTotalUnpaidPaymentsAmount = (
  enrollment: Enrollment,
  payFor: PayFor,
  includeAdminFee: boolean = true,
  client?: Client
) => {
  if (isEnrollmentFullyRefunded(enrollment)) {
    return 0
  }

  if (isEnrollmentCancelled(enrollment)) {
    return 0
  }

  // Sum all finalPrice values based on payFor
  // Only include activities that are confirmed (not pending approval)
  const sumOfFinalPrices = enrollment.priceDetails.reduce((total, pd) => {
    // Skip activities that are not confirmed
    if (!isActivityConfirmed(enrollment, pd.activityId)) {
      return total
    }

    let price = 0
    if (payFor === PayFor.Both) {
      price = pd.finalPrice
    } else if (payFor === PayFor.Main && pd.appliesTo === 'mainUser') {
      price = pd.finalPrice
    } else if (payFor === PayFor.Partner && pd.appliesTo === 'partnerUser') {
      price = pd.finalPrice
    }
    return total + price
  }, 0)

  const administrationFee =
    includeAdminFee && shouldIncludeStoredAdministrationFee(sumOfFinalPrices, client)
      ? enrollment.priceDetails.reduce((total, pd) => {
          // Skip activities that are not confirmed
          if (!isActivityConfirmed(enrollment, pd.activityId)) {
            return total
          }

          let fee = 0
          if (payFor === PayFor.Both) {
            fee = pd.administrationFee || 0
          } else if (payFor === PayFor.Main && pd.appliesTo === 'mainUser') {
            fee = pd.administrationFee || 0
          } else if (payFor === PayFor.Partner && pd.appliesTo === 'partnerUser') {
            fee = pd.administrationFee || 0
          }
          return total + fee
        }, 0)
      : 0

  const totalPrice = sumOfFinalPrices + administrationFee

  const netPaid = getUserNetPaidAmount(enrollment, payFor)
  return totalPrice - netPaid
}

export const convertEnrollmentToEnrollmentPublic = (enrollment: Enrollment): EnrollmentPublic => {
  return {
    id: enrollment.id,
    createdAt: enrollment.createdAt,
    updatedAt: dayjs().format(),
    paymentStatus: enrollment.paymentStatus,
    ocr: enrollment.ocr,
    status: enrollment.status,
    clientId: enrollment.clientId
  }
}

export type UserPaymentStatus = {
  paid: number
  due: number
  balance: number
  paymentGroups: Record<string, number>
  status: 'paid' | 'notPaid' | 'partlyPaid' | 'unknown'
  overflow: { source: string; amount: number }
}

export const getUserPaymentStatus = (
  enrollment: EnrollmentPaymentStatusView,
  payFor: PayFor,
  client?: Client
): UserPaymentStatus => {
  const userPayments = getUserPayments(enrollment, payFor)
  let userPaid = getUserNetPaidAmount(enrollment, payFor)
  // Sum finalPrice values based on payFor (admin fee is per transaction, not per price detail)
  const sumOfFinalPrices = enrollment.priceDetails.reduce((total, pd) => {
    // Only include activities that are confirmed (not pending approval)
    if (!isActivityConfirmed(enrollment, pd.activityId)) {
      return total
    }

    if (
      (payFor === PayFor.Main && pd.appliesTo === 'mainUser') ||
      (payFor === PayFor.Partner && pd.appliesTo === 'partnerUser')
    ) {
      total += pd.finalPrice
    }
    return total
  }, 0)

  const administrationFee = shouldIncludeStoredAdministrationFee(sumOfFinalPrices, client)
    ? enrollment.priceDetails.reduce((total, pd) => {
        // Only count admin fees from relevant price details based on payFor
        let shouldInclude = false
        if (payFor === PayFor.Main && pd.appliesTo === 'mainUser') {
          shouldInclude = true
        } else if (payFor === PayFor.Partner && pd.appliesTo === 'partnerUser') {
          shouldInclude = true
        }

        // Only include admin fees for confirmed activities
        if (shouldInclude && pd.administrationFee && isActivityConfirmed(enrollment, pd.activityId)) {
          return total + pd.administrationFee
        }
        return total
      }, 0)
    : 0

  // Total due = sum of final prices + sum of all admin fees (per transaction)
  const userDue = sumOfFinalPrices + administrationFee
  const userPaymentSources = userPayments.reduce(
    (groups, payEntry) => {
      const source = payEntry.payment.source
      if (!groups[source]) {
        groups[source] = 0
      }
      groups[source] += payEntry.paid
      return groups
    },
    {} as Record<string, number>
  )
  let userBalance = userDue - userPaid

  // split payment
  let userOverflow = {} as { source: string; amount: number }
  if (payFor == PayFor.Main) {
    // if user paid too much, probably it is paid for "both", so we need to split it
    if (userPaid > userDue) {
      // iterate over all payment sources and extract the paid amount
      let required = userDue
      for (const [key, value] of Object.entries(userPaymentSources)) {
        const extract = Math.min(value, required)
        required -= extract
        const paymentLeft = userPaymentSources[key] - extract
        if (paymentLeft > 0) {
          userOverflow.source = key
          userOverflow.amount = (userOverflow.amount || 0) + paymentLeft
        }
      }
    }
  }

  // initial result
  const result: UserPaymentStatus = {
    paid: userPaid,
    due: userDue,
    balance: userBalance,
    paymentGroups: userPaymentSources,
    status: 'unknown',
    overflow: userOverflow
  }

  if (payFor === PayFor.Main) {
    if (userBalance <= 0) {
      // case 3: mainUser: paid by mainUser (exact balance)
      result.status = 'paid'
    } else if (userPaid > 0) {
      // case 3: mainUser: paid by mainUser (some amount)
      result.status = 'partlyPaid'
    } else {
      // case 1: mainUser: not paid
      result.status = 'notPaid'
    }
  }

  if (payFor == PayFor.Partner) {
    if (enrollment.mainUser.id === enrollment.partnerUser?.id) {
      // fix some values
      // Calculate mainUserDue using the same logic as userDue above
      const mainUserFinalPrices = enrollment.priceDetails
        .filter((pd) => pd.appliesTo === 'mainUser')
        .reduce((total, pd) => {
          // Keep it consistent with `userDue` above: only confirmed activities contribute here.
          if (!isActivityConfirmed(enrollment, pd.activityId)) return total
          total += pd.finalPrice
          return total
        }, 0)

      const mainUserAdminFee = shouldIncludeStoredAdministrationFee(mainUserFinalPrices, client)
        ? enrollment.priceDetails
            .filter((pd) => pd.appliesTo === 'mainUser')
            .reduce((total, pd) => {
              if (!isActivityConfirmed(enrollment, pd.activityId)) return total
              total += pd.administrationFee || 0
              return total
            }, 0)
        : 0

      let mainUserDue = mainUserFinalPrices + mainUserAdminFee

      // userDue -= mainUserDue
      if (userPaid > 0) {
        userPaid -= mainUserDue
      }
      userBalance = userDue - userPaid
      result.paid = userPaid
      result.balance = userBalance

      // iterate over all payment sources and extract the paid amount
      let required = mainUserDue
      for (const [key, value] of Object.entries(result.paymentGroups)) {
        const extract = Math.min(value, required)
        required -= extract
        result.paymentGroups[key] = result.paymentGroups[key] - extract
      }
    }

    if (userBalance <= 0) {
      // case 4: partnerUser: paid by partnerUser (exact balance)
      result.status = 'paid'
    } else if (userPaid > 0) {
      result.status = 'partlyPaid'
    } else if (userBalance > 0) {
      // check if not paid by mainUser
      const mainUserStatus = getUserPaymentStatus(enrollment, PayFor.Main, client)
      const mainUserOverflow = mainUserStatus.overflow
      // balance < 0 means that mainUser paid for himself and there are still some unpaid amounts for partnerUser
      //if (mainUserBalance < 0) {
      if (mainUserOverflow.amount > 0) {
        // update partnerUser balance state
        userBalance -= mainUserOverflow.amount
        userPaid += mainUserOverflow.amount
        result.balance = userBalance
        result.paid = userPaid
        result.paymentGroups[mainUserOverflow.source] = mainUserOverflow.amount

        if (userBalance <= 0) {
          // case 5: partnerUser: paid by mainUser (main+partner=exact balance)
          result.status = 'paid'
        } else if (userPaid > 0) {
          result.status = 'partlyPaid'
        } else {
          // case 2a: partner: not paid, nor by mainUser
          result.status = 'notPaid'
        }
      } else {
        result.status = 'notPaid'
      }
    }
  }

  return result
}

/**
 * Same as getUserPaymentStatus, but includes ALL activities in due calculation,
 * even if they're pending (not approved).
 *
 * Intended for UI cases where we want to show the full potential total amount in compact lists.
 */
export const getUserPaymentStatusIncludingPending = (
  enrollment: Enrollment,
  payFor: PayFor,
  client?: Client
): UserPaymentStatus => {
  const userPayments = getUserPayments(enrollment, payFor)
  let userPaid = getUserNetPaidAmount(enrollment, payFor)

  // Sum finalPrice values based on payFor (admin fee is per transaction, not per price detail)
  // NOTE: Unlike getUserPaymentStatus, we do NOT filter by activity approval status here.
  // But we DO exclude activities explicitly marked as cancelled/deleted/refunded (statuses-only rule).
  const sumOfFinalPrices = enrollment.priceDetails.reduce((total, pd) => {
    if (isActivityExcludedFromValue(enrollment, pd.activityId)) {
      return total
    }
    if (
      (payFor === PayFor.Main && pd.appliesTo === 'mainUser') ||
      (payFor === PayFor.Partner && pd.appliesTo === 'partnerUser')
    ) {
      total += pd.finalPrice
    }
    return total
  }, 0)

  const administrationFee = shouldIncludeStoredAdministrationFee(sumOfFinalPrices, client)
    ? enrollment.priceDetails.reduce((total, pd) => {
        if (isActivityExcludedFromValue(enrollment, pd.activityId)) {
          return total
        }
        let shouldInclude = false
        if (payFor === PayFor.Main && pd.appliesTo === 'mainUser') {
          shouldInclude = true
        } else if (payFor === PayFor.Partner && pd.appliesTo === 'partnerUser') {
          shouldInclude = true
        }

        if (shouldInclude && pd.administrationFee) {
          return total + pd.administrationFee
        }
        return total
      }, 0)
    : 0

  const userDue = sumOfFinalPrices + administrationFee
  const userPaymentSources = userPayments.reduce(
    (groups, payEntry) => {
      const source = payEntry.payment.source
      if (!groups[source]) {
        groups[source] = 0
      }
      groups[source] += payEntry.paid
      return groups
    },
    {} as Record<string, number>
  )
  let userBalance = userDue - userPaid

  // split payment
  let userOverflow = {} as { source: string; amount: number }
  if (payFor == PayFor.Main) {
    if (userPaid > userDue) {
      let required = userDue
      for (const [key, value] of Object.entries(userPaymentSources)) {
        const extract = Math.min(value, required)
        required -= extract
        const paymentLeft = userPaymentSources[key] - extract
        if (paymentLeft > 0) {
          userOverflow.source = key
          userOverflow.amount = (userOverflow.amount || 0) + paymentLeft
        }
      }
    }
  }

  const result: UserPaymentStatus = {
    paid: userPaid,
    due: userDue,
    balance: userBalance,
    paymentGroups: userPaymentSources,
    status: 'unknown',
    overflow: userOverflow
  }

  if (payFor === PayFor.Main) {
    if (userBalance <= 0) {
      result.status = 'paid'
    } else if (userPaid > 0) {
      result.status = 'partlyPaid'
    } else {
      result.status = 'notPaid'
    }
  }

  if (payFor == PayFor.Partner) {
    if (enrollment.mainUser.id === enrollment.partnerUser?.id) {
      // Calculate mainUserDue using the same logic as userDue above
      const mainUserFinalPrices = enrollment.priceDetails
        .filter((pd) => pd.appliesTo === 'mainUser' && !isActivityExcludedFromValue(enrollment, pd.activityId))
        .reduce((total, pd) => {
          total += pd.finalPrice
          return total
        }, 0)

      const mainUserAdminFee = shouldIncludeStoredAdministrationFee(mainUserFinalPrices, client)
        ? enrollment.priceDetails
            .filter((pd) => pd.appliesTo === 'mainUser' && !isActivityExcludedFromValue(enrollment, pd.activityId))
            .reduce((total, pd) => {
              total += pd.administrationFee || 0
              return total
            }, 0)
        : 0

      let mainUserDue = mainUserFinalPrices + mainUserAdminFee

      if (userPaid > 0) {
        userPaid -= mainUserDue
      }
      userBalance = userDue - userPaid
      result.paid = userPaid
      result.balance = userBalance

      let required = mainUserDue
      for (const [key, value] of Object.entries(result.paymentGroups)) {
        const extract = Math.min(value, required)
        required -= extract
        result.paymentGroups[key] = result.paymentGroups[key] - extract
      }
    }

    if (userBalance <= 0) {
      result.status = 'paid'
    } else if (userPaid > 0) {
      result.status = 'partlyPaid'
    } else if (userBalance > 0) {
      const mainUserStatus = getUserPaymentStatusIncludingPending(enrollment, PayFor.Main, client)
      const mainUserOverflow = mainUserStatus.overflow
      if (mainUserOverflow.amount > 0) {
        userBalance -= mainUserOverflow.amount
        userPaid += mainUserOverflow.amount
        result.balance = userBalance
        result.paid = userPaid
        result.paymentGroups[mainUserOverflow.source] = mainUserOverflow.amount

        if (userBalance <= 0) {
          result.status = 'paid'
        } else if (userPaid > 0) {
          result.status = 'partlyPaid'
        } else {
          result.status = 'notPaid'
        }
      } else {
        result.status = 'notPaid'
      }
    }
  }

  return result
}

export const getVatFromBruttoPrice = (brutto: number, vat: number) => {
  const vatCalc = (brutto * vat) / (100 + vat)
  const vatValue = Math.round(vatCalc * 100) / 100

  return vatValue
}

export const calculateEnrollmentVATTotal = (enrollment: Enrollment): number => {
  const activities = enrollment.activities || []
  const vatAmount = enrollment.priceDetails.reduce((sum, pd) => {
    const activityVat = activities.find((activity) => activity.id === pd.activityId)?.vat || 0
    sum += getVatFromBruttoPrice(pd.finalPrice, activityVat) / 100
    sum = Math.round(sum * 100) / 100
    return sum
  }, 0)
  return Number(vatAmount.toFixed(2))
}

export const calculateEnrollmentVATGroups = (enrollment: Enrollment): VatData => {
  const activities = enrollment.activities || []
  const vat = enrollment.priceDetails.reduce((vatData, pd) => {
    const activityVat = activities.find((activity) => activity.id === pd.activityId)?.vat || 0
    if (!vatData[activityVat]) vatData[activityVat] = 0
    vatData[activityVat] += getVatFromBruttoPrice(pd.finalPrice, activityVat) / 100
    vatData[activityVat] = Math.round(vatData[activityVat] * 100) / 100
    return vatData
  }, {} as VatData)

  return vat
}

const TERMINAL_STATUS_SEVERITY: EnrollmentStatus[] = [
  EnrollmentStatus.Deleted,
  EnrollmentStatus.CancelledWithRefund,
  EnrollmentStatus.CancelledWithCredit,
  EnrollmentStatus.ClassCancelled,
  EnrollmentStatus.Cancelled
]

const TERMINAL_ENROLLMENT_STATUSES = new Set<EnrollmentStatus>(TERMINAL_STATUS_SEVERITY)

export const calculateEnrollmentStatusFromEnrollmentIndividualStatuses = (enrollment: Enrollment): EnrollmentStatus => {
  const statuses = enrollment.statuses || []
  debugLog(`[DEBUG] calculateEnrollmentStatusFromEnrollmentIndividualStatuses called:`, {
    enrollmentId: enrollment.id,
    currentStatus: enrollment.status,
    statuses: statuses,
    activitiesApprovalRequired: enrollment.activities.map((a) => ({
      id: a.id,
      name: a.name,
      approvalRequired: a.approvalRequired
    }))
  })

  if (statuses.length === 0) {
    const normalized = normalizeEnrollmentStatus(enrollment.status)
    debugLog(`[DEBUG] No individual statuses found, returning existing status:`, normalized)
    return normalized
  }

  const normalized = statuses.map((s) => normalizeEnrollmentStatus(s.status))

  // All same (after normalizing legacy Refunded)
  if (normalized.every((s) => s === normalized[0])) {
    debugLog(`[DEBUG] All statuses are the same, returning:`, normalized[0])
    return normalized[0]
  }

  const terminals = normalized.filter((s) => TERMINAL_ENROLLMENT_STATUSES.has(s))
  const nonTerminals = normalized.filter((s) => !TERMINAL_ENROLLMENT_STATUSES.has(s))

  if (terminals.length > 0) {
    if (nonTerminals.length === 0) {
      for (const severity of TERMINAL_STATUS_SEVERITY) {
        if (terminals.includes(severity)) {
          debugLog(`[DEBUG] Mixed terminals, returning highest severity:`, severity)
          return severity
        }
      }
    }
    // Terminal + live/approval → still partially on roster / pending
    debugLog(`[DEBUG] Mix of terminal and non-terminal statuses, returning PartlyApproved`)
    return EnrollmentStatus.PartlyApproved
  }

  const hasApprovedStatuses = normalized.some((s) => s === EnrollmentStatus.Confirmed)
  const allStatusesApproved = normalized.every((s) => s === EnrollmentStatus.Confirmed)
  const allStatusesPending = normalized.every((s) => s === EnrollmentStatus.Pending)

  if (allStatusesApproved) {
    debugLog(`[DEBUG] All statuses are approved, returning Confirmed`)
    return EnrollmentStatus.Confirmed
  }

  if (allStatusesPending) {
    debugLog(`[DEBUG] All statuses are pending, returning Pending`)
    return EnrollmentStatus.Pending
  }

  if (hasApprovedStatuses) {
    debugLog(`[DEBUG] At least one status is approved, returning PartlyApproved`)
    return EnrollmentStatus.PartlyApproved
  }

  debugLog(`[DEBUG] Default case, returning Confirmed`)
  return EnrollmentStatus.Confirmed
}

/**
 * Wraps calculateEnrollmentStatusFromEnrollmentIndividualStatuses with a payment-driven override:
 * a fully refunded enrollment is reported as CancelledWithRefund regardless of its per-activity statuses,
 * unless it's Deleted (deletion always wins). Legacy Refunded is normalized away.
 */
export const resolveEnrollmentStatus = (enrollment: Enrollment): EnrollmentStatus => {
  const baseStatus = calculateEnrollmentStatusFromEnrollmentIndividualStatuses(enrollment)
  const normalized = normalizeEnrollmentStatus(baseStatus)
  if (normalized === EnrollmentStatus.Deleted) return normalized
  return isEnrollmentFullyRefunded(enrollment) ? EnrollmentStatus.CancelledWithRefund : normalized
}

/**
 * What a payment-settlement path needs to know about an enrollment an admin already soft-deleted.
 *
 * Deletion is soft (`EnrollmentStatus.Deleted`, either on the enrollment or per-activity in
 * `statuses`), so the document stays readable and payable long after it has dropped off every
 * roster and reminder query (those filter `status != Deleted`, see `RegistrationDBHandler`).
 * Registration mails carry a live payment link and go out *before* payment, so a customer can pay
 * a link for an enrollment that was deleted as an abandoned duplicate in the meantime.
 */
export type EnrollmentDeletionInfo = {
  /** The whole enrollment reads as deleted — it is off every roster and reminder query. */
  isFullyDeleted: boolean
  /** activityIds explicitly marked Deleted in `statuses`; empty when only the top-level status is Deleted. */
  deletedActivityIds: string[]
}

/**
 * Deletion facts for a settlement path, or `undefined` when nothing about the enrollment is deleted.
 *
 * Covers both shapes admin-web can leave behind: the legacy whole-enrollment delete
 * (`status: Deleted`, no `statuses`) and the per-activity delete that writes a `Deleted` entry into
 * `statuses`. A non-empty `statuses` array stays authoritative over a stale top-level `status` —
 * the same rule the rest of the codebase uses.
 *
 * Partial deletion still returns a value with `isFullyDeleted: false`: the enrollment is on the
 * roster, but the money may be paying for an activity that no longer is.
 */
export const getEnrollmentDeletionInfo = (enrollment: Enrollment): EnrollmentDeletionInfo | undefined => {
  const statuses = enrollment.statuses || []
  const deletedActivityIds = statuses.filter((s) => s.status === EnrollmentStatus.Deleted).map((s) => s.activityId)

  // Same "statuses wins when present" rule as calculateEnrollmentStatusFromEnrollmentIndividualStatuses,
  // spelled out here rather than delegated: this runs from webhook settlement paths, where reading a
  // field the status calculator happens to touch (`activities`) would turn a Slack alert into a throw.
  const isFullyDeleted =
    statuses.length > 0
      ? statuses.every((s) => normalizeEnrollmentStatus(s.status) === EnrollmentStatus.Deleted)
      : normalizeEnrollmentStatus(enrollment.status) === EnrollmentStatus.Deleted

  if (!isFullyDeleted && deletedActivityIds.length === 0) return undefined

  return { isFullyDeleted, deletedActivityIds }
}

export const generatePaymentId = (enrollment: Enrollment, payment: Payment): string => {
  const paymentIndex = enrollment.payments?.findIndex((p) => p === payment) ?? -1
  if (paymentIndex === -1) return ''
  return `${enrollment.id}-${paymentIndex + 1}`
}

export const generateUniquePaymentRef = (): string => {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 8)
  return `manual-${timestamp}-${random}`
}
